import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ImageIcon, Images } from 'lucide-react';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { useBrandImagery } from '@shared/hooks/useBrandImagery';
import { pageCopy } from '@shared/lib/pageCopy';
import { fetchGalleryAlbums, type GalleryAlbumSummary } from './api';

const MONTH_FMT = new Intl.DateTimeFormat(undefined, { month: 'short', year: 'numeric' });

function albumDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : MONTH_FMT.format(d);
}

/**
 * Estate gallery — "Our moments". A photo header, a large featured album,
 * then a two-column grid of album cards with photo counts.
 */
export function GalleryLanding(): JSX.Element {
  const copy = pageCopy().gallery;
  const navigate = useNavigate();
  const location = useLocation();
  const { heroImage } = useBrandImagery();
  const query = useQuery({ queryKey: ['gallery-albums'], queryFn: fetchGalleryAlbums });
  const backTo =
    typeof location.state === 'object' &&
    location.state !== null &&
    'backTo' in location.state &&
    typeof location.state.backTo === 'string'
      ? location.state.backTo
      : '/app/home';

  const albums = query.data ?? [];
  const [featured, ...rest] = albums;

  return (
    <div className="min-h-full bg-paper">
      {/* ── Photo header ─────────────────────────────────────────── */}
      <HeroHeader
        eyebrow={copy.eyebrow}
        title={copy.title}
        subtitle={copy.subtitle}
        backgroundImage={heroImage}
        backTo={backTo}
      />

      <div className="px-5 pt-5 pb-safe-or-6">
        {query.isLoading ? (
          <div className="flex flex-col gap-4">
            <Skeleton className="h-52" />
            <div className="grid grid-cols-2 gap-3">
              <Skeleton className="h-44" />
              <Skeleton className="h-44" />
            </div>
          </div>
        ) : query.isError ? (
          <ErrorState onRetry={() => query.refetch()} />
        ) : albums.length === 0 ? (
          <EmptyState
            icon={<Images className="h-7 w-7" strokeWidth={1.5} />}
            title="No albums yet."
            description="Estate photo albums will appear here."
          />
        ) : (
          <>
            {featured ? (
              <button
                type="button"
                onClick={() => navigate(`/app/gallery/${featured.album_id}`)}
                className="relative mb-4 flex aspect-[16/10] w-full flex-col justify-end overflow-hidden rounded-2xl bg-sunken text-left"
              >
                {featured.cover_image_url ? (
                  <img
                    src={featured.cover_image_url}
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                ) : null}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="relative p-4">
                  <span className="rounded-pill bg-white/20 px-2.5 py-1 text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-white backdrop-blur">
                    Featured
                  </span>
                  <p className="display-serif mt-2 text-display-lg text-white">{featured.name}</p>
                  <p className="text-body-sm text-white/75">
                    {[
                      featured.photo_count ? `${featured.photo_count} photos` : null,
                      albumDate(featured.updated_at),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
              </button>
            ) : null}

            {rest.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">
                {rest.map((album) => (
                  <AlbumCard
                    key={album.album_id}
                    album={album}
                    onClick={() => navigate(`/app/gallery/${album.album_id}`)}
                  />
                ))}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

function AlbumCard({
  album,
  onClick,
}: {
  album: GalleryAlbumSummary;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col text-left transition-transform duration-fast active:scale-[0.98]"
    >
      <div className="relative aspect-square w-full overflow-hidden rounded-xl bg-sunken">
        {album.cover_image_url ? (
          <img src={album.cover_image_url} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-primary-50">
            <ImageIcon className="h-7 w-7 text-primary-600" strokeWidth={1.5} />
          </div>
        )}
        {album.photo_count ? (
          <span className="absolute bottom-2 right-2 flex items-center gap-1 rounded-md bg-black/70 px-2 py-0.5 text-caption font-medium text-white backdrop-blur">
            <ImageIcon className="h-3 w-3" strokeWidth={2} />
            {album.photo_count}
          </span>
        ) : null}
      </div>
      <p className="mt-2 font-display text-body leading-tight text-ink line-clamp-2">{album.name}</p>
      {albumDate(album.updated_at) ? (
        <p className="text-caption text-ink-muted">{albumDate(album.updated_at)}</p>
      ) : null}
    </button>
  );
}
